import React from 'react';
import { mergeClasses } from 'minimal-shared/utils';

import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { Theme, SxProps } from '@mui/material/styles';

import { createClasses } from 'src/theme/createClasses';

import useLocales from '../../../hooks/useLocales';
import useResponsive from '../../../hooks/useResponsive';
import SvgIconStyle from '../../../components/SvgIconStyle';
import UploadIllustration from '../../../assets/illustrations/upload-illustration';

// ----------------------------------------------------------------------

export type UploadPlaceholderProps = React.ComponentProps<'div'> & {
  isWidget: boolean;
  sx?: SxProps<Theme>;
};

const uploadPlaceholderClasses = {
  root: createClasses('upload__placeholder__root'),
  content: createClasses('upload__placeholder__content'),
  title: createClasses('upload__placeholder__title'),
  description: createClasses('upload__placeholder__description'),
};

export function UploadPlaceholder({ isWidget, sx, className, ...other }: UploadPlaceholderProps) {
  const { t } = useLocales();
  const theme = useTheme();
  const isMobile = useResponsive('only', 'xs');

  if (isWidget || isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
        <Box flexGrow={1} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'}>
            {t('dlxDrive.buttons.uploadDocument')}
          </Typography>

          {!isMobile && (
            <Typography variant="body2" color={theme.palette.text.secondary}>
              <Typography
                id="Hdwe51"
                variant="inherit"
                component="span"
                color={theme.palette.primary.main}
                sx={{ textDecoration: 'underline' }}
              >
                {t('documents.choose')}
              </Typography>
              {t('documents.docOrDrag')}
            </Typography>
          )}
        </Box>

        <Box>
          <SvgIconStyle
            src="/icons/dlx-drive/upload_document.svg"
            sx={{
              width: isMobile ? 36 : 54,
              height: isMobile ? 36 : 54,
              display: 'inherit',
              opacity: '0.5',
              color: theme.palette.grey[600],
            }}
          />
        </Box>
      </Box>
    );
  }

  return (
    <PlaceholderRoot
      className={mergeClasses([uploadPlaceholderClasses.root, className])}
      sx={sx}
      {...other}
    >
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        <UploadIllustration hideBackground sx={{ width: 140 }} />

        <PlaceholderContent>
          <div className={uploadPlaceholderClasses.title}>{t('documents.upload.uploadFiles')}</div>
          <div className={uploadPlaceholderClasses.description}>
            {t('components.uploadSingleFile.dropFilesHere')}
            <span>{t('components.uploadSingleFile.browse')}</span>
            {t('components.uploadSingleFile.thoroughYourMachine')}
          </div>

          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="caption" sx={{ color: theme.palette.grey[600] }}>
              {`${t('documents.upload.allowedFileTypes')}: *.pdf, *.doc, *.docx, *.xls, *.xlsx, *.ppt, *.pptx, *.jpeg, *.jpg, *.png, *.adoc, *.edoc, *.bdoc, *.asice, *.txt`}
            </Typography>

            <Typography variant="caption" sx={{ color: theme.palette.grey[600] }}>
              {`${t('documents.upload.maxSize')}: 50Mb`}
            </Typography>
          </Box>
        </PlaceholderContent>
      </Box>
    </PlaceholderRoot>
  );
}

// ----------------------------------------------------------------------

const PlaceholderRoot = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
}));

const PlaceholderContent = styled('div')(({ theme }) => ({
  display: 'flex',
  textAlign: 'center',
  gap: theme.spacing(1),
  flexDirection: 'column',
  [`& .${uploadPlaceholderClasses.title}`]: { ...theme.typography.h6 },
  [`& .${uploadPlaceholderClasses.description}`]: {
    ...theme.typography.body2,
    color: theme.vars.palette.text.secondary,
    '& span': {
      textDecoration: 'underline',
      margin: theme.spacing(0, 0.5),
      color: theme.vars.palette.primary.main,
    },
  },
}));
